import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { askMoo } from '../../../queries/ask-moo';
import {
  updateUserMemory,
  setUserMemoryEnabled,
  deleteUserMemory,
} from '../api';
import { getData } from '../../../utils/functional/fp';
import { useAuth } from '../../hooks/useAuth';

export function useUserMemory() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    ...askMoo.userMemory(),
    enabled: isAuthenticated,
  });
}

/**
 * After any successful mutation we re-fetch GET /user-memory rather than
 * patching the cache: the backend bumps `version` (and recomputes
 * `tokenCount`) on write, so a fresh read is the source of truth.
 */
function useInvalidateUserMemory() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: askMoo.userMemory().queryKey });
}

export function useUpdateUserMemory() {
  const invalidate = useInvalidateUserMemory();
  return useMutation({
    mutationFn: (profileMd: string) =>
      updateUserMemory({ data: { profileMd } }).then(getData),
    onSuccess: invalidate,
  });
}

export function useSetUserMemoryEnabled() {
  const invalidate = useInvalidateUserMemory();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      setUserMemoryEnabled({ data: { enabled } }).then(getData),
    onSuccess: invalidate,
  });
}

export function useDeleteUserMemory() {
  const invalidate = useInvalidateUserMemory();
  return useMutation({
    mutationFn: () => deleteUserMemory().then(getData),
    onSuccess: invalidate,
  });
}
